import Axios from "axios";
import toast from "react-hot-toast";
import i18n from "../i18n/i18n";

const axios = Axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  timeout: Number(process.env.REACT_APP_REQUEST_TIMEOUT),
});

/**
 * Langue active, telle que l'API doit la comprendre.
 *
 * `localStorage.lang` d'abord : c'est le choix explicite de l'utilisateur
 * dans le selecteur, et il survit a un rechargement avant meme que i18next
 * ait fini de s'initialiser. `i18n.language` ensuite, ampute de sa variante
 * regionale (`fr-FR` -> `fr`) parce que l'API et l'agent ne publient qu'un
 * catalogue par langue.
 *
 * Exporte parce que l'intercepteur ci-dessous n'est PAS le seul appelant :
 * le transport SSE de l'assistant IA utilise `fetch` brut et ne passe donc
 * jamais par axios. Deux copies de cette regle, c'est la garantie qu'une
 * seule sera corrigee le jour ou elle changera.
 */
export const currentLanguage = (): string | null =>
  localStorage.getItem("lang") || i18n.language?.split("-")[0] || null;

axios.interceptors.request.use((config) => {
  // Read token for anywhere, in this case directly from localStorage
  const token = localStorage.getItem("token");
  if (token) {
    config.headers["X-token-api"] = token;
  }
  const lang = currentLanguage();
  if (lang) {
    // use config.params if it has been set
    config.params = config.params || {};
    // add any client instance specific params to config
    config.params["lang"] = lang;
  }

  return config;
});

// ─── Session-expiration guard ────────────────────────────────────────
// When multiple requests fire concurrently (e.g. navigating to a page
// that fetches vehicles, events and sinisters at once), a single 401
// is enough to trigger a logout. Without a guard, every concurrent 401
// would call localStorage.clear() + reload(), causing a reload storm.
//
// The flag below ensures we execute the logout sequence exactly once.
let isLoggingOut = false;

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Only handle genuine authentication failures (401).
    // Authorization failures (403) mean "you don't have the right role"
    // and must NOT trigger a logout — the user IS authenticated, they
    // just lack the permission for this specific resource.
    if (error.response && error.response.status === 401 && !isLoggingOut) {
      isLoggingOut = true;
      toast.error(
        i18n.t("login.sessionExpired", "Session expired, please login again")
      );
      localStorage.clear();
      // Small delay so the toast is visible before the reload
      setTimeout(() => {
        window?.location?.reload();
      }, 300);
    }
    return Promise.reject(error);
  }
);

export default axios;
